/* ============================================================
   nmp.js — the shared behaviour layer.

   Five jobs, and deliberately nothing else:

   1. <nmp-overlay> and its three subclasses — nav overlay, search overlay
      and cart drawer. docs/03 §Cross-cutting behaviours requires every
      interactive panel to share one contract: focus trap, Esc to close,
      scrim click to close, aria-expanded/aria-controls, body scroll lock.
      Implementing that once here is what keeps the three panels consistent
      and the JS budget small (CLAUDE.md: any single page loads <= 16KB
      brotli, comments stripped — a per-page gate, not an assets/ total).

   2. --header-h — the sticky offset. tokens.css ships a static fallback so
      the page is correct before this file runs; here we measure the real
      header and write the exact value, because the utility row and the
      countdown bar are both merchant-toggleable.

   3. Theme-editor hooks. The overlays and the drawer are invisible by
      default, so without shopify:section:select they would look broken to
      the owner in the editor — which is the owner's #1 requirement.

   4. NmpAdoptSectionStyles — see below. Every file that swaps part of a
      fetched section has to call it, so it lives here rather than in any
      one of them.

   5. data-nmp-modality on <html> — 'pointer' or 'keyboard', whichever the
      visitor last used. tokens.css holds the focus ring back while it reads
      'pointer', because the browser's own :focus-visible heuristic cannot
      tell a tap from a keypress for the focus job 1 moves by script. It is
      here rather than in a file of its own because job 1 is what makes it
      necessary and this file is the one that loads on every page.

   Progressive enhancement: with JS off, the nav and search overlays still
   open via CSS :target (their triggers are real <a href="#…"> links) and the
   bag is a real link to /cart. This file calls preventDefault() on those
   triggers so the URL hash never appears when scripting is on, and since
   2026-09-06 reconciles a panel that is already :target — the account is
   sections/nav-overlay.liquid, header item 1.

   Note there is no aria-hidden management: the panels are hidden with
   `display:none`, which already removes them from the accessibility tree,
   and a hardcoded aria-hidden in the markup would wrongly hide the :target
   fallback from screen readers when JS is off.

   Last updated: 2026-09-14
   ============================================================ */
(function () {
  'use strict';

  var root = document.documentElement;

  var FOCUSABLE = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /* Every currently open overlay, most recent last. */
  var openStack = [];

  function visibleFocusables(el) {
    return Array.prototype.filter.call(el.querySelectorAll(FOCUSABLE), function (node) {
      return node.offsetWidth > 0 || node.offsetHeight > 0 || node.getClientRects().length > 0;
    });
  }

  /* Scroll lock, reference counted and exported. Two independent things lock
     the page — the overlay stack here and the PDP zoom dialog in product.js —
     and a boolean attribute cannot express "two holders", so whichever
     released first unlocked the page under the other. */
  var lockCount = 0;

  function lockScroll() {
    lockCount += 1;
    root.setAttribute('data-nmp-locked', '');
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) root.removeAttribute('data-nmp-locked');
  }

  /* The stack holds at most ONE reference however many panels are open:
     openStack already models the nesting, and counting each panel would leave
     the page locked after the last one closed. */
  var stackHolds = false;

  function syncScrollLock() {
    var wants = openStack.length > 0;
    if (wants === stackHolds) return;
    stackHolds = wants;
    if (wants) lockScroll();
    else unlockScroll();
  }

  /* ---------------------------------------------------- input modality */

  /* `:focus-visible` asks the browser to GUESS whether a focus was keyboard
     driven, and on a touch device it guesses wrong about the focus this very
     file moves: open() focuses the first control inside the panel and close()
     hands focus back to the trigger. On desktop the mouse event immediately
     before each of those calls settles the question and the ring stays down.
     A tap produces no mouse event, so the script-moved focus reads as
     keyboard and the accent ring paints around the MENU button, the bag and
     every close control — a red stroke chasing the tap, in ss26-brick.

     So record the modality ourselves and let tokens.css hold the ring back
     only while it is 'pointer'. pointerdown covers mouse, touch and pen in
     one listener and always fires before the click that opens a panel, so the
     attribute is correct by the time open() moves focus; keydown flips it
     straight back, which is what gives a Bluetooth keyboard on a phone its
     ring on the first Tab. Capture phase, because a handler nearer the target
     may stop the event and this one has to see it either way.

     Nothing is written until an interaction happens, so no attribute means
     the unconditional ring — which is what layout/password.liquid gets (it
     loads capture.js alone, not this file) and what any engine without
     Pointer Events gets. docs/02 §The focus ring is modality-gated has the
     rest, including why this is not a media query. */
  function setModality(kind) {
    if (root.getAttribute('data-nmp-modality') !== kind) {
      root.setAttribute('data-nmp-modality', kind);
    }
  }

  document.addEventListener('pointerdown', function () { setModality('pointer'); }, true);
  document.addEventListener('keydown', function () { setModality('keyboard'); }, true);

  /* Exported like NmpAdoptSectionStyles: nmp.js is unconditional in the
     layout and product.js is not, so the dependency only points one way. */
  window.NmpScrollLock = { lock: lockScroll, unlock: unlockScroll };

  /* ------------------------------------------------- section stylesheets */

  /* Shopify subsets the CSS collected from stylesheet tags so that each page
     only carries the styles of the files in its own render tree. That is a
     real win and a real trap: markup fetched later can contain a file the
     page never rendered, and it arrives unstyled.

     The cart drawer is the clearest case. Its line loop only runs when the
     bag has something in it, so on any page loaded with an empty bag
     snippets/cart-line.liquid is not in the render tree and none of its CSS
     is in styles.css — and the first item added would land naked. The
     predictive results section is worse still: it is never in any page's
     render tree, because nothing renders it until someone types.

     Shopify's answer is to ship the section's own CSS with the response in a
     style[data-section-stylesheet] element, which is included automatically
     if you insert the whole response and has to be lifted out by hand if you
     insert part of it. We always insert part of it, so this runs on every
     successful swap.

     Keyed by section id and replaced rather than appended, so repeating a
     swap a hundred times leaves exactly one style element behind. */
  function adoptSectionStyles(doc, sectionId) {
    if (!doc || !sectionId) return;
    var incoming = doc.querySelector('style[data-section-stylesheet]');
    if (!incoming) return;

    var id = 'nmp-adopted-' + sectionId;
    var live = document.getElementById(id);
    if (!live) {
      live = document.createElement('style');
      live.id = id;
      live.setAttribute('data-nmp-adopted', sectionId);
      document.head.appendChild(live);
    }
    if (live.textContent !== incoming.textContent) live.textContent = incoming.textContent;
  }

  /* ---------------------------------------------------------- overlay */

  class NmpOverlay extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this._onKeydown = this._onKeydown.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onHash = this._onHash.bind(this);

      this.addEventListener('click', function (event) {
        if (event.target.closest('[data-nmp-close]')) {
          event.preventDefault();
          /* :target-shown but never open()ed: close() would no-op. */
          if (!this.isOpen && this.matches(':target')) this._clearHash();
          this.close();
        }
      }.bind(this));

      this.addEventListener('mousedown', this._onPointerDown);

      window.addEventListener('hashchange', this._onHash);
      this._onHash();
    }

    /* Panel is :target (tap before this ran, shared URL, bfcache): take the
       state over, or [data-open] and :target disagree and close() can't hide it. */
    _onHash() {
      if (!this.id || window.location.hash !== '#' + this.id) return;
      if (this.isOpen) this._clearHash(); else this.open();
    }

    /* A real navigation — only that re-evaluates :target; replace() adds no
       history entry. */
    _clearHash() {
      window.location.replace(window.location.pathname + window.location.search + '#');
    }

    disconnectedCallback() {
      this._teardown();
    }

    get isOpen() {
      return this.hasAttribute('data-open');
    }

    /* The control(s) that point at this panel via aria-controls. */
    get triggers() {
      if (!this.id) return [];
      return Array.prototype.slice.call(
        document.querySelectorAll('[aria-controls="' + this.id + '"]')
      );
    }

    open(options) {
      if (this.isOpen) return;
      options = options || {};

      /* Only one panel at a time — opening the bag closes the menu. */
      openStack.slice().forEach(function (other) {
        if (other !== this) other.close({ restoreFocus: false });
      }, this);

      this._lastFocused = options.restoreTo || document.activeElement;

      this.setAttribute('data-open', '');
      if (this.id && window.location.hash === '#' + this.id) this._clearHash();
      openStack.push(this);
      syncScrollLock();

      this.triggers.forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      });

      document.addEventListener('keydown', this._onKeydown);

      /* Let the panel paint before moving focus, so the transition is seen
         and assistive tech announces a settled element. */
      window.requestAnimationFrame(function () {
        var target = this.querySelector('[data-nmp-autofocus]') || visibleFocusables(this)[0];
        if (target) target.focus({ preventScroll: true });
      }.bind(this));

      this.dispatchEvent(new CustomEvent('nmp:open', { bubbles: true }));
    }

    close(options) {
      if (!this.isOpen) return;
      options = options || {};

      this.removeAttribute('data-open');
      openStack = openStack.filter(function (item) { return item !== this; }, this);
      syncScrollLock();

      this.triggers.forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      });

      document.removeEventListener('keydown', this._onKeydown);

      if (options.restoreFocus !== false && this._lastFocused && this._lastFocused.focus) {
        this._lastFocused.focus({ preventScroll: true });
      }
      this._lastFocused = null;

      this.dispatchEvent(new CustomEvent('nmp:close', { bubbles: true }));
    }

    toggle() {
      if (this.isOpen) this.close(); else this.open();
    }

    _teardown() {
      document.removeEventListener('keydown', this._onKeydown);
      window.removeEventListener('hashchange', this._onHash);
      openStack = openStack.filter(function (item) { return item !== this; }, this);
      syncScrollLock();

      /* Triggers outlive the panel: removed while open, this left the header
         buttons claiming aria-expanded="true" for nothing. */
      this.triggers.forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      });
      this.removeAttribute('data-open');
    }

    /* Click on the scrim (the panel's own backdrop) closes it. Using
       mousedown on the scrim only — not a document listener — so a drag that
       starts inside the panel and ends on the scrim does not close it. */
    _onPointerDown(event) {
      if (event.target.hasAttribute && event.target.hasAttribute('data-nmp-scrim')) {
        this.close();
      }
    }

    _onKeydown(event) {
      /* Only the topmost panel responds. */
      if (openStack[openStack.length - 1] !== this) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      var items = visibleFocusables(this);
      if (items.length === 0) {
        event.preventDefault();
        return;
      }

      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;

      /* The `!this.contains(active)` half matters as much as the first/last
         half. Focus can be outside an open panel — the browser's address bar
         hands it back to the document, a script moved it, the editor
         re-rendered the section underneath — and until Phase 5 only the
         backwards branch checked for that, so forward Tab walked off into the
         page behind the scrim. Both directions pull it back now. */
      var outside = !this.contains(active);

      if (event.shiftKey && (active === first || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  class NmpNavOverlay extends NmpOverlay {}
  class NmpSearchOverlay extends NmpOverlay {}
  class NmpCartDrawer extends NmpOverlay {}

  /* One guard per name. Three defines behind a single get() of the FIRST name
     holds only while all three ship together — register one elsewhere and this
     silently skips two or throws NotSupportedError. Phase 8d showed this repo
     splits files under budget pressure. */
  if (!window.customElements.get('nmp-nav-overlay')) {
    window.customElements.define('nmp-nav-overlay', NmpNavOverlay);
  }
  if (!window.customElements.get('nmp-search-overlay')) {
    window.customElements.define('nmp-search-overlay', NmpSearchOverlay);
  }
  if (!window.customElements.get('nmp-cart-drawer')) {
    window.customElements.define('nmp-cart-drawer', NmpCartDrawer);
  }

  /* ---------------------------------------------------- triggers */

  /* Delegated so triggers can live anywhere — the header, the footer, an
     empty-cart CTA — without each section wiring its own listener. */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-nmp-open]');
    if (!trigger) return;

    var panel = document.getElementById(trigger.getAttribute('data-nmp-open'));
    if (!panel || typeof panel.open !== 'function') return;

    /* Swallow the href="#Panel" navigation: that anchor exists purely as the
       no-JS fallback and we do not want the hash in the URL. */
    event.preventDefault();
    if (panel.isOpen) panel.close(); else panel.open({ restoreTo: trigger });
  });

  /* ---------------------------------------------------- header height */

  var headerRaf = null;

  function measureHeader() {
    var header = document.querySelector('[data-nmp-header]');
    if (!header) return;
    var height = Math.round(header.getBoundingClientRect().height);
    if (height > 0) root.style.setProperty('--header-h', height + 'px');
  }

  function scheduleMeasure() {
    if (headerRaf) window.cancelAnimationFrame(headerRaf);
    headerRaf = window.requestAnimationFrame(measureHeader);
  }

  scheduleMeasure();
  window.addEventListener('resize', scheduleMeasure, { passive: true });
  window.addEventListener('load', scheduleMeasure);

  /* Held and re-attachable: created once at module scope, it watched a
     detached node as soon as the editor re-rendered the header. */
  var headerObserver = null;

  function observeHeader() {
    if (!('ResizeObserver' in window)) return;
    if (headerObserver) headerObserver.disconnect();
    var headerEl = document.querySelector('[data-nmp-header]');
    if (!headerEl) return;
    if (!headerObserver) headerObserver = new ResizeObserver(scheduleMeasure);
    headerObserver.observe(headerEl);
  }

  observeHeader();

  /* ------------------------------------------- transparent header */

  /* When the header is set to sit over a full-bleed hero it starts
     transparent and takes its background once the page moves. Driven by a
     single attribute so all the styling stays in the section.

     The element is looked up once and cached. This runs on every scroll
     event, and a querySelector per frame down a long collection page is
     thousands of tree walks to answer a question whose answer never changes.
     Re-resolved on section load so the theme editor still tracks it. */
  var transparentHeader = null;

  function findTransparentHeader() {
    transparentHeader = document.querySelector('[data-nmp-header][data-transparent]');
  }

  function syncHeaderScrolled() {
    if (!transparentHeader) return;
    if (window.scrollY > 8) {
      transparentHeader.setAttribute('data-scrolled', '');
    } else {
      transparentHeader.removeAttribute('data-scrolled');
    }
  }

  findTransparentHeader();
  syncHeaderScrolled();
  window.addEventListener('scroll', syncHeaderScrolled, { passive: true });

  /* ---------------------------------------------------- theme editor */

  function panelIn(sectionEl) {
    if (!sectionEl || !sectionEl.querySelector) return null;
    return sectionEl.querySelector('nmp-nav-overlay, nmp-search-overlay, nmp-cart-drawer');
  }

  document.addEventListener('shopify:section:select', function (event) {
    var panel = panelIn(event.target);
    if (panel) panel.open();
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    var panel = panelIn(event.target);
    if (panel) panel.close();
  });

  /* Same contract for blocks, and a sharper case: a block inside one of these
     panels is display:none until it opens. closest() rather than panelIn() —
     here the panel is the target's ancestor, not its descendant. */
  document.addEventListener('shopify:block:select', function (event) {
    var panel = event.target.closest && event.target.closest('nmp-nav-overlay, nmp-search-overlay, nmp-cart-drawer');
    if (panel && typeof panel.open === 'function') panel.open();
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var panel = event.target.closest && event.target.closest('nmp-nav-overlay, nmp-search-overlay, nmp-cart-drawer');
    if (panel && typeof panel.close === 'function') panel.close();
  });

  document.addEventListener('shopify:section:load', function () {
    scheduleMeasure();
    /* The header section may have just been re-rendered, which replaces the
       element the scroll handler holds and the one the observer watches. */
    findTransparentHeader();
    syncHeaderScrolled();
    observeHeader();
  });
  document.addEventListener('shopify:section:unload', function () {
    scheduleMeasure();
    /* Same in reverse — a stale reference writes to a detached node. */
    findTransparentHeader();
    observeHeader();
  });

  /* Expose the base class so later phases (PDP zoom dialog, filter sheet)
     reuse the same contract instead of reimplementing it, and the stylesheet
     helper so every file that swaps part of a section uses one copy of it. */
  window.NmpOverlay = NmpOverlay;
  window.NmpAdoptSectionStyles = adoptSectionStyles;
})();
